import React, { useEffect, useRef, useState } from 'react';
import styles from './ParallaxHero.module.css';
import {
  dayPC, nightPC,
  daySmall, nightSmall,
  dayExtraSmall, nightExtraSmall,
  dayImages, nightImages,
  audio,
} from './ParallaxConfig';

const logo = '/Icons/logo/Zodiaclogo.png';

interface ParallaxHeroProps {
  onScrollToContent?: () => void;
}

const ParallaxHero: React.FC<ParallaxHeroProps> = ({ onScrollToContent }) => {
  const [parallaxProgress, setParallaxProgress] = useState(0);
  const [isLogoHovered, setIsLogoHovered] = useState(false);
  const [screenSize, setScreenSize] = useState<'pc' | 'small' | 'extraSmall'>('pc');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [audioStarted, setAudioStarted] = useState(false);
  const [audioEnabled] = useState(true);

  // Detect theme
  useEffect(() => {
    const checkTheme = () => {
      const theme = document.documentElement.getAttribute('data-theme');
      setIsDarkMode(theme === 'dark');
    };
    checkTheme();

    // Watch for theme changes
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });

    return () => observer.disconnect();
  }, []);

  // Detect screen size
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      if (width <= 480) {
        setScreenSize('extraSmall');
      } else if (width <= 768) {
        setScreenSize('small');
      } else {
        setScreenSize('pc');
      }
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // DEBUG: Log ANY scroll/wheel input from user
  useEffect(() => {
    const findScrollingElement = () => {
      // Check all possible scroll containers
      const checks = [
        { name: 'window.scrollY', value: window.scrollY },
        { name: 'document.documentElement.scrollTop', value: document.documentElement.scrollTop },
        { name: 'document.body.scrollTop', value: document.body.scrollTop },
      ];

      // Also check any element with overflow that might be scrolling
      const allElements = document.querySelectorAll('*');
      allElements.forEach(el => {
        const htmlEl = el as HTMLElement;
        if (htmlEl.scrollTop > 0) {
          checks.push({
            name: `${htmlEl.tagName}.${htmlEl.className?.split(' ')[0] || 'no-class'}`,
            value: htmlEl.scrollTop
          });
        }
      });

      return checks.filter(c => c.value > 0);
    };

    const handleWheel = (e: WheelEvent) => {
      const scrollingElements = findScrollingElement();
      const target = e.target as HTMLElement;
    };

    const handleScrollInput = () => {
      const scrollingElements = findScrollingElement();    
    };

    window.addEventListener('wheel', handleWheel, { passive: true });
    window.addEventListener('scroll', handleScrollInput, { passive: true });

    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('scroll', handleScrollInput);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!heroRef.current) return;

      const heroRect = heroRef.current.getBoundingClientRect();
      const heroHeight = heroRef.current.offsetHeight;
      const viewportHeight = window.innerHeight;

      // Calculate how far through the hero section we've scrolled
      // When hero top is at viewport top, progress = 0
      // When hero bottom is at viewport top, progress = 1
      const heroTopFromViewportTop = heroRect.top;
      const progress = Math.min(Math.max(-heroTopFromViewportTop / heroHeight +0.5, 0), 1);

      setParallaxProgress(progress);

      // Proximity audio: Calculate how centered the hero is in viewport
      // heroCenterY = center of hero relative to viewport
      const heroCenterY = heroRect.top + heroHeight / 2;
      const viewportCenterY = viewportHeight / 2;

      // Distance from center (0 = perfectly centered, increases as you scroll away)
      const distanceFromCenter = Math.abs(heroCenterY - viewportCenterY);

      // Max distance is when hero is completely off screen (heroHeight/2 + viewportHeight/2)
      const maxDistance = heroHeight / 2 + viewportHeight / 2;

      // Proximity: 1 = centered, 0 = far away
      const proximity = Math.max(0, 1 - (distanceFromCenter / maxDistance));

      // Volume: 80% max when centered, fades to 0 when off-screen
      if (audioRef.current) {
        // Only play if visible, pause if not
        if (proximity > 0.1) {
          audioRef.current.volume = proximity * 0.8;
          if (audioRef.current.paused && audioStarted) {
            audioRef.current.play().catch(() => {});
          }
        } else {
          audioRef.current.volume = 0;
        }
      }
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [audioStarted]);

  // Reload audio when theme changes
  useEffect(() => {
    if (audioRef.current && audioStarted) {
      audioRef.current.load();
      audioRef.current.play().catch(() => {});
    }
  }, [isDarkMode]);

  // Start audio on first user interaction (browser autoplay policy)
  useEffect(() => {
    if (!audioEnabled) return;

    const startAudio = () => {
      if (audioRef.current && !audioStarted) {
        audioRef.current.play().catch(() => {});
        setAudioStarted(true);
      }
    };

    // Try to start on any user interaction (only for returning users)
    window.addEventListener('scroll', startAudio, { once: true });
    window.addEventListener('click', startAudio, { once: true });
    window.addEventListener('touchstart', startAudio, { once: true });

    return () => {
      window.removeEventListener('scroll', startAudio);
      window.removeEventListener('click', startAudio);
      window.removeEventListener('touchstart', startAudio);
    };
  }, [audioStarted, audioEnabled]);


  // ============================================
  // GET CONFIG FROM ParallaxConfig.ts
  // ============================================
  // All settings are now in ParallaxConfig.ts - edit them there!

  // Get the right config based on theme and screen size
  const getActiveConfig = () => {
    if (isDarkMode) {
      switch (screenSize) {
        case 'pc': return nightPC;
        case 'small': return nightSmall;
        case 'extraSmall': return nightExtraSmall;
      }
    } else {
      switch (screenSize) {
        case 'pc': return dayPC;
        case 'small': return daySmall;
        case 'extraSmall': return dayExtraSmall;
      }
    }
  };

  const config = getActiveConfig();
  const images = isDarkMode ? nightImages : dayImages;

  // Positions from config
  const positions = {
    sky: config.skyPosition,
    mountain: config.mountainPosition,
    forest: config.forestPosition,
    cliff: config.cliffPosition,
  };

  // Movement offsets (multiply by scroll progress)
  const skyOffset = config.skyMovement * parallaxProgress;
  const mountainOffset = config.mountainMovement * parallaxProgress;
  const forestOffset = config.forestMovement * parallaxProgress;
  const cliffOffset = config.cliffMovement * parallaxProgress;

  return (
    <div ref={heroRef} className={styles.parallaxContainer}>
      {/* Proximity audio - different ambience for day/night */}
      <audio
        ref={audioRef}
        src={isDarkMode ? audio.nightAmbience : audio.dayAmbience}
        loop
        preload="auto"
      />

      {/* Side banners */}
      <div className={`${styles.sideBanner} ${styles.left}`} />
      <div className={`${styles.sideBanner} ${styles.right}`} />

      {/* Parallax viewport - narrower center area, tappable on mobile to toggle glow */}
      <div
        className={styles.parallaxViewport}
        onClick={() => {
          // Toggle highlight effect on mobile devices (tap anywhere)
          if (screenSize !== 'pc') {
            setIsLogoHovered(prev => !prev);
          }
        }}
      >
        {/* Sky layer - back */}
        <div
          className={styles.parallaxLayer}
          style={{
            backgroundImage: `url(${images.sky})`,
            backgroundPosition: 'center center',
            top: positions.sky,
            transform: `translateY(${skyOffset}px)`,
            filter: isLogoHovered ? 'brightness(0.92) contrast(1.08)' : 'none',
            transition: 'filter 0.3s ease',
          }}
        />

        {/* Mountain layer - base */}
        <div
          className={styles.parallaxLayer}
          style={{
            backgroundImage: `url(${images.mountain})`,
            backgroundPosition: 'center center',
            top: positions.mountain,
            transform: `translateY(${mountainOffset}px)`,
          }}
        />

        {/* Mountain layer - hover overlay (fades in on logo hover) */}
        <div
          className={`${styles.parallaxLayer} ${styles.lightMountain}`}
          style={{
            backgroundImage: `url(${images.mountainShine})`,
            backgroundPosition: 'center center',
            top: positions.mountain,
            transform: `translateY(${mountainOffset}px)`,
            opacity: isLogoHovered ? 0.8 : 0,
          }}
        />

        {/* Forest/Trees layer */}
        <div
          className={styles.parallaxLayer}
          style={{
            backgroundImage: `url(${images.forest})`,
            backgroundPosition: 'center center',
            top: positions.forest,
            transform: `translateY(${forestOffset}px)`,
          }}
        />

        {/* Forest layer - shine overlay (fades in on logo hover) */}
        {images.forestShine && (
          <div
            className={`${styles.parallaxLayer} ${styles.lightForest}`}
            style={{
              backgroundImage: `url(${images.forestShine})`,
              backgroundPosition: 'center center',
              top: positions.forest,
              transform: `translateY(${forestOffset}px)`,
              opacity: isLogoHovered ? (isDarkMode ? 0.4 : 0.6) : 0,
            }}
          />
        )}

        {/* Cliff layer - only show if image exists */}
        {images.cliff && (
          <>
            <div
              className={styles.parallaxLayer}
              style={{
                backgroundImage: `url(${images.cliff})`,
                backgroundPosition: 'center center',
                top: positions.cliff,
                transform: `translateY(${cliffOffset}px)`,
              }}
            />

            {/* Cliff layer - shine overlay (fades in on logo hover) */}
            {images.cliffShine && (
              <div
                className={`${styles.parallaxLayer} ${styles.lightCliff}`}
                style={{
                  backgroundImage: `url(${images.cliffShine})`,
                  backgroundPosition: 'center center',
                  top: positions.cliff,
                  transform: `translateY(${cliffOffset}px)`,
                  opacity: isLogoHovered ? 0.5 : 0,
                }}
              />
            )}
          </>
        )}

        {/* Logo overlay */}
        <div className={styles.logoContainer}>
          <img
            src={logo}
            alt="Zodiac Chess"
            className={`${styles.logo} ${isLogoHovered ? styles.logoGlowing : ''}`}
            onMouseEnter={() => {
              // Only use hover on PC - mobile uses tap on viewport
              if (screenSize === 'pc') {
                setIsLogoHovered(true);
              }
            }}
            onMouseLeave={() => {
              // Only use hover on PC - mobile uses tap on viewport
              if (screenSize === 'pc') {
                setIsLogoHovered(false);
              }
            }}
            draggable={false}
          />
          <span className={styles.logoText}>ZODIAC</span>
        </div>

        {/* Scroll indicator - stop propagation so it doesn't toggle glow */}
        <div
          className={styles.scrollIndicator}
          onClick={(e) => {
            e.stopPropagation();
            onScrollToContent?.();
          }}
        >
          <div className={styles.scrollArrow}>▼</div>
        </div>

      </div>
    </div>
  );
};

export default ParallaxHero;
